import { showPageLoader } from './index';
import { logError, logException } from '../services/sentryService';
import cookies from './cookies';

const getMobileAppOs = () => {
  return (cookies.get('MOBILE_APP') || '').toLowerCase();
};

const hasNonPrimitiveArgs = (args = []) =>
  args.some((arg) => arg !== null && typeof arg === 'object');

// Android JS interfaces accept primitives only, so object payloads must be
// serialized before they cross the bridge.
const normalizeBridgeArgumentForAndroid = (argument, functionName) => {
  if (argument === null || typeof argument !== 'object') {
    return argument;
  }

  try {
    return JSON.stringify(argument);
  } catch (error) {
    logError(
      `Express Checkout: failed to serialize ${functionName} argument: ${
        error?.message || error
      }`
    );
    return argument;
  }
};

const normalizeMobileAppBridgeArgs = (functionName, args = []) => {
  if (!hasNonPrimitiveArgs(args) || !getMobileAppOs().includes('android')) {
    return args;
  }

  return args.map((argument) =>
    normalizeBridgeArgumentForAndroid(argument, functionName)
  );
};

const submitPlaceOrderForm = () => {
  try {
    showPageLoader();
    $('#js-placeOrderForm').submit();
  } catch (error) {
    logError(
      `Express Checkout with Mobile Apps: Error on placeOrder ${error.message}`
    );
  }
};

function notifyMobileAppOnPreviewPageSuccess() {
  const errorCode = window.expressCheckoutPlaceOrderErrorCode;

  if (errorCode) {
    logError(
      `Express Checkout: error code present on preview page: ${errorCode}`
    );
    return;
  }

  callMobileAppJSBridge('previewPageSuccess');
}

const exposeCallbackFunctionsForMobileApp = () => {
  const paymentFieldMap = {
    APPLE_PAY: '#js-applePayOrderId',
    PAYPAL: '#js-paypalOrderId',
  };

  window.onPayCollected = (paymentMethod, paymentData = {}) => {
    try {
      const tokenField = paymentFieldMap[paymentMethod];

      if (!tokenField) {
        return;
      }

      const { payTokenId, paymentPlatformType } = paymentData;

      if (!payTokenId || !paymentPlatformType) {
        sendPreviewValidationErrorToMobileApp('INVALID_PAYMENT_DATA', true);
        return;
      }

      $(tokenField).val(payTokenId);
      $('#js-paymentPlatformType').val(paymentPlatformType);

      submitPlaceOrderForm();
    } catch (error) {
      logException(
        `Express Checkout with Mobile Apps: Error in onPayCollected ${error.message}`,
        {
          area: 'checkout',
          payment: `Express Checkout with Mobile Apps - ${paymentMethod}`,
        }
      );
    }
  };
};

const handleExpressCheckoutOrderPlacementErrorCodeToMobileApp = () => {
  const errorCode = window.expressCheckoutPlaceOrderErrorCode;

  if (!errorCode) {
    return;
  }

  sendPreviewValidationErrorToMobileApp(
    errorCode,
    window.expressCheckoutIsRetryableError
  );
};

const sendPreviewValidationErrorToMobileApp = (errorCode, isRetryable) => {
  try {
    callMobileAppJSBridge('previewValidationError', errorCode, isRetryable);
  } catch (err) {
    logError(
      `Failed to send express checkout error code to mobile app: ${
        err?.message || err
      }`
    );
  }
};

const getAppExpressCheckoutToggleStatus = () => {
  const isExpressCheckoutAppEnabled = getMobileAppBridgeFunction(
    'isExpressCheckoutAppEnabled'
  );

  return typeof isExpressCheckoutAppEnabled === 'function'
    ? isExpressCheckoutAppEnabled()
    : false;
};

const getMobileAppBridgeFunction = (functionName) => {
  if (
    window.zooplusApp &&
    typeof window.zooplusApp[functionName] === 'function'
  ) {
    return window.zooplusApp[functionName].bind(window.zooplusApp);
  }

  if (typeof window[functionName] === 'function') {
    return window[functionName].bind(window);
  }

  return;
};

export const callMobileAppJSBridge = (functionName, ...args) => {
  const fn = getMobileAppBridgeFunction(functionName);
  if (typeof fn !== 'function') {
    logError(`Express Checkout: ${functionName} is missing from mobile bridge`);
    return;
  }

  const normalizedArgs = normalizeMobileAppBridgeArgs(functionName, args);
  return fn(...normalizedArgs);
};

export const ExpressCheckout = {
  init: () => {
    window.isExpressCheckoutOnMobileAppsEnabled =
      getAppExpressCheckoutToggleStatus();

    const { isExpressCheckoutOnMobileAppsEnabled } = window;
    if (!isExpressCheckoutOnMobileAppsEnabled) {
      return;
    }

    const currentUrl = window.location.href.toLowerCase();
    const isPreviewPage = currentUrl.includes('preview');
    const isEditPaymentPage = currentUrl.includes('editpayment');
    const isEditInvoiceAddressPage = currentUrl.includes('editaddress');
    const isEditDeliveryAddressPage = currentUrl.includes(
      'editshippingaddress'
    );

    if (isPreviewPage) {
      exposeCallbackFunctionsForMobileApp();
      notifyMobileAppOnPreviewPageSuccess();
    }

    const isExpressCheckoutTouchPointPages =
      isPreviewPage ||
      isEditPaymentPage ||
      isEditInvoiceAddressPage ||
      isEditDeliveryAddressPage;

    if (isExpressCheckoutTouchPointPages) {
      handleExpressCheckoutOrderPlacementErrorCodeToMobileApp();
    }
  },
};
